import { Box2, Vector2 } from 'three';

export class Range2 {
  constructor(public min: number, public max: number) {}
}
export enum Direction {
  Up = 1,
  Down = 2,
  Left = 3,
  Right = 4,
}
export interface Rectangle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
export interface BoundingBox2 extends Rectangle {
  box: Box2;
  center: Vector2;
  size: Vector2;
}
export function intersectBoundingBox2(p: Vector2, b: BoundingBox2) {
  return b.box.containsPoint(p);
}

export class BoundingBox2FromBox2 implements BoundingBox2 {
  constructor(public box: Box2) {}
  get center() {
    return new Vector2(
      (this.box.min.x + this.box.max.x) / 2,
      (this.box.min.y + this.box.max.y) / 2,
    );
  }
  set center(v: Vector2) {
    const offset = new Vector2(v.x - this.center.x, v.y - this.center.y);
    this.box.min.add(offset);
    this.box.max.add(offset);
  }
  get size() {
    return new Vector2(
      this.box.max.x - this.box.min.x,
      this.box.max.y - this.box.min.y,
    );
  }
  set size(v: Vector2) {
    this.update(new BoundingBox2FromCenterSize(this.center, v));
  }
  set left(v) {
    this.box.min.x = v;
  }
  set right(v) {
    this.box.max.x = v;
  }
  set top(v) {
    this.box.min.x = v;
  }
  set bottom(v) {
    this.box.max.x = v;
  }
  get left() {
    return this.box.min.x;
  }
  get right() {
    return this.box.max.x;
  }
  get top() {
    return this.box.min.y;
  }
  get bottom() {
    return this.box.max.y;
  }
  update(bb: BoundingBox2) {
    this.box = bb.box.clone();
  }
}
export class BoundingBox2FromCenterSize implements BoundingBox2 {
  public center: Vector2;
  public size: Vector2;
  constructor(center: Vector2, size: Vector2) {
    this.center = center.clone();
    this.size = size.clone();
  }
  get box() {
    return new Box2(
      new Vector2(this.left, this.top),
      new Vector2(this.right, this.bottom),
    );
  }
  set box(box2: Box2) {
    this.copy(new BoundingBox2FromBox2(box2));
  }
  get left() {
    return this.center.x - this.size.x / 2;
  }
  get right() {
    return this.center.x + this.size.x / 2;
  }
  get top() {
    return this.center.y - this.size.y / 2;
  }
  get bottom() {
    return this.center.y + this.size.y / 2;
  }
  set left(v) {
    const result = new BoundingBox2FromLRTB(
      v,
      this.right,
      this.top,
      this.bottom,
    );
    this.copy(result);
  }

  set right(v) {
    this.copy(new BoundingBox2FromLRTB(this.left, v, this.top, this.bottom));
  }
  set top(v) {
    this.copy(new BoundingBox2FromLRTB(this.left, this.right, v, this.bottom));
  }
  set bottom(v) {
    this.copy(new BoundingBox2FromLRTB(this.left, this.right, this.top, v));
  }

  updateRight(v) {
    return new BoundingBox2FromLRTB(this.left, v, this.top, this.bottom);
  }
  updateLeft(v) {
    return new BoundingBox2FromLRTB(v, this.right, this.top, this.bottom);
  }
  updateBottom(v) {
    return new BoundingBox2FromLRTB(this.left, this.right, this.top, v);
  }
  updateTop(v) {
    return new BoundingBox2FromLRTB(this.left, this.right, v, this.bottom);
  }
  copy(bb: BoundingBox2) {
    this.center = bb.center.clone();
    this.size = bb.size.clone();
  }
}
export class BoundingBox2FromLRTB implements BoundingBox2 {
  constructor(
    public left: number,
    public right: number,
    public top: number,
    public bottom: number,
  ) {}
  get box() {
    return new Box2(
      new Vector2(this.left, this.top),
      new Vector2(this.right, this.bottom),
    );
  }
  get center() {
    return new Vector2(
      (this.left + this.right) / 2,
      (this.top + this.bottom) / 2,
    );
  }
  get size() {
    return new Vector2(this.right - this.left, this.bottom - this.top);
  }
  set size(v: Vector2) {
    this.copy(new BoundingBox2FromCenterSize(this.center, v));
  }
  set center(v: Vector2) {
    this.copy(new BoundingBox2FromCenterSize(v, this.size));
  }
  copy(bb: BoundingBox2) {
    this.left = bb.left;
    this.right = bb.right;
    this.top = bb.top;
    this.bottom = bb.bottom;
  }
}

export class Boundingbox2Controller {
  public leftBox: BoundingBox2;
  public rightBox: BoundingBox2;
  public bottomBox: BoundingBox2;
  public topBox: BoundingBox2;
  public anyBox: BoundingBox2;
  constructor(public box: BoundingBox2, public scale: number) {}
  leftController() {
    return new BoundingBox2FromLRTB(
      this.box.left - this.box.size.x * this.scale,
      this.box.left,
      this.box.top,
      this.box.bottom,
    );
  }
  rightController() {
    return new BoundingBox2FromLRTB(
      this.box.right,
      this.box.right + this.box.size.x * this.scale,
      this.box.top,
      this.box.bottom,
    );
  }
  bottomController() {
    return new BoundingBox2FromLRTB(
      this.box.left,
      this.box.right,
      this.box.bottom,
      this.box.bottom + this.box.size.y * this.scale,
    );
  }
  topController() {
    return new BoundingBox2FromLRTB(
      this.box.left,
      this.box.right,
      this.box.top - this.box.size.y * this.scale,
      this.box.top,
    );
  }
  anyController() {
    return new BoundingBox2FromLRTB(
      this.box.left * this.scale,
      this.box.left,
      this.box.top,
      this.box.bottom,
    );
  }
  onWhichAreabox(position: Vector2) {
    if (intersectBoundingBox2(position, this.leftController())) {
      return 'left';
    }
    if (intersectBoundingBox2(position, this.rightController())) {
      return 'right';
    }
    if (intersectBoundingBox2(position, this.topController())) {
      return 'top';
    }
    if (intersectBoundingBox2(position, this.bottomController())) {
      return 'bottom';
    }
    if (intersectBoundingBox2(position, this.box)) {
      return 'inside';
    }
    return 'none';
  }
}

export function moveBoundingBox2By(box: BoundingBox2, offset: Vector2) {
  return new BoundingBox2FromCenterSize(
    new Vector2(box.center.x + offset.x, box.center.y + offset.y),
    box.size,
  );
}

export function moveBoundingBox2To(box: BoundingBox2, target: Vector2) {
  return new BoundingBox2FromCenterSize(target, box.size);
}
